import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSession } from '@/providers/AppSessionProvider';
import { useNotificationPromptStore } from '@/stores/notificationPromptStore';
import { NotificationPromptDialog } from '@/components/NotificationPromptDialog';
import { detectNativeApp, waitForNotificationsSDK } from '@/lib/platform';
import { oneSignalService } from '@/lib/onesignal';
import { toast } from 'sonner';

/**
 * Component that tracks app visits and triggers notification prompt
 * on the third visit (if not already shown via social tab).
 * This should be mounted at the app level.
 */
export const AppVisitTracker = () => {
  const { t } = useTranslation();
  const { ready, isAuthenticated } = useAppSession();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const {
    incrementVisitCount,
    shouldShowPromptOnThirdVisit,
    hasShownPrompt,
  } = useNotificationPromptStore();

  useEffect(() => {
    // Only track visits when session is ready and user is authenticated
    if (ready !== 'ready' || !isAuthenticated) return;

    // Don't increment if we've already shown the prompt
    if (hasShownPrompt) return;

    // Increment visit count
    incrementVisitCount();

    // Check if we should show the prompt after third visit
    const shouldShow = shouldShowPromptOnThirdVisit();
    if (!shouldShow) return;

    // Only show in native app
    const checkAndShowPrompt = async () => {
      const detection = await detectNativeApp();
      if (!detection.isNative) {
        console.log('[AppVisitTracker] Not in native app, skipping notification prompt');
        return;
      }

      // Check if we already have permission
      try {
        const sdkLoaded = await waitForNotificationsSDK(5000);
        if (sdkLoaded) {
          const initialized = await oneSignalService.initialize();
          if (initialized) {
            const hasPermission = await oneSignalService.getPermissionStatus();
            if (hasPermission) {
              console.log('[AppVisitTracker] Permission already granted, skipping prompt');
              return;
            }
          }
        }
      } catch (error) {
        console.error('[AppVisitTracker] Error checking permission:', error);
      }

      // Wait 2 seconds after third visit before showing prompt
      setTimeout(() => {
        console.log('[AppVisitTracker] Triggering notification prompt after third visit');
        setShowNotificationPrompt(true);
      }, 2000);
    };

    checkAndShowPrompt();
  }, [ready, isAuthenticated, hasShownPrompt, incrementVisitCount, shouldShowPromptOnThirdVisit]);

  return (
    <NotificationPromptDialog
      open={showNotificationPrompt}
      onOpenChange={setShowNotificationPrompt}
      onSuccess={() => {
        toast.success(t('notifications.toast.notifications_enabled_follow_posts'));
      }}
      onError={(error) => {
        // Only show error if it's not a permission denial
        if (!error.includes("denied")) {
          toast.error(error);
        }
      }}
    />
  );
};
